import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderOpen, Upload, FileText, FileSpreadsheet, Trash2, Loader2,
  Download, ArrowLeft, Sparkles, Files,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const FOLDER_ACCENT = {
  Common: "bg-slate-100 text-slate-600",
  Mathematics: "bg-indigo-100 text-indigo-600",
  Physics: "bg-sky-100 text-sky-600",
  Chemistry: "bg-teal-100 text-teal-600",
  Biology: "bg-emerald-100 text-emerald-600",
  "Individuals & Societies": "bg-orange-100 text-orange-600",
  "Language & Literature": "bg-purple-100 text-purple-600",
  "Language Acquisition": "bg-fuchsia-100 text-fuchsia-600",
  "Physical & Health Education": "bg-rose-100 text-rose-600",
  "Design & Arts": "bg-pink-100 text-pink-600",
};

const prettySize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function Documents() {
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [uploadFolder, setUploadFolder] = useState("Common");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/documents/folders").then((r) => setFolders(r.data));
    api.get("/documents").then((r) => setDocs(r.data));
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("subject", active || uploadFolder);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} added to ${active || uploadFolder}`);
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success("Document removed");
    load();
  };

  const openDoc = (id) => window.open(`${API}/documents/${id}/download`, "_blank");

  const visible = active ? docs.filter((d) => d.subject === active) : [];

  return (
    <div className="space-y-6">
      {/* Upload bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="doc-upload-bar">
        <div className="flex items-center gap-2 text-slate-700 font-medium">
          <Upload className="w-4 h-4 text-indigo-500" /> Add a document to
        </div>
        <Select value={active || uploadFolder} onValueChange={active ? undefined : setUploadFolder} disabled={!!active}>
          <SelectTrigger data-testid="upload-folder-select" className="rounded-xl sm:w-64"><SelectValue /></SelectTrigger>
          <SelectContent>
            {folders.map((f) => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.png,.jpg,.jpeg,.xlsx,.docx" onChange={upload} className="hidden" data-testid="doc-file-input" />
        <Button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 sm:ml-auto">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Upload file
        </Button>
      </div>

      {!active ? (
        /* Folder grid */
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="folder-grid">
          {folders.map((f, i) => (
            <motion.button
              key={f.name}
              data-testid={`folder-${f.name}`}
              onClick={() => setActive(f.name)}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-md transition-transform"
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${FOLDER_ACCENT[f.name] || "bg-slate-100 text-slate-600"}`}>
                <FolderOpen className="w-6 h-6" />
              </div>
              <p className="font-heading font-semibold text-slate-900 leading-tight">{f.name}</p>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Files className="w-3 h-3" /> {f.count} file{f.count !== 1 ? "s" : ""}</p>
            </motion.button>
          ))}
        </div>
      ) : (
        /* Folder contents */
        <div>
          <div className="flex items-center gap-3 mb-4">
            <button data-testid="folder-back-btn" onClick={() => setActive(null)} className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-50">
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${FOLDER_ACCENT[active] || "bg-slate-100 text-slate-600"}`}>
              <FolderOpen className="w-5 h-5" />
            </div>
            <h3 className="font-heading font-bold text-xl text-slate-900">{active}</h3>
            <span className="text-sm text-slate-400">({visible.length})</span>
          </div>

          <div className="space-y-2" data-testid="folder-docs">
            <AnimatePresence>
              {visible.length === 0 && (
                <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
                  <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No documents here yet. Upload one above.</p>
                </div>
              )}
              {visible.map((d) => {
                const isSheet = (d.original_filename || "").match(/\.(xlsx|csv)$/i);
                const FileIcon = isSheet ? FileSpreadsheet : FileText;
                return (
                  <motion.div
                    key={d.id}
                    layout initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                    className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500 shrink-0">
                      <FileIcon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{d.original_filename}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {prettySize(d.size) && <span className="text-xs text-slate-400">{prettySize(d.size)}</span>}
                        {d.source === "provided" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600">PROVIDED</span>}
                      </div>
                    </div>
                    <button data-testid={`doc-open-${d.id}`} onClick={() => openDoc(d.id)} title="Open / download" className="w-9 h-9 rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 flex items-center justify-center transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                    <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)} title="Delete" className="w-9 h-9 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 flex items-center justify-center transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          <p className="text-xs text-slate-400 mt-4 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-purple-400" /> Tip: uploaded PDFs & notes can also be used by the AI Tutor to answer questions.
          </p>
        </div>
      )}
    </div>
  );
}
